import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

import { POST_LOGIN_REDIRECT_KEY } from "../../../../constants/postLoginRedirect";
import {
  checkAccountStatus,
  fetchCouponPlanId,
  requestReactivationPaymentLink,
} from "../../api";
import {
  CAPICANDIA_COUPONS,
  CAPICANDIA_VARIANTS,
  getCapicandiaCheckoutPeriodicity,
  isCapicandiaSpecialCoupon,
} from "../../validation";
import {
  trackCapicandiaOnboardingAnswered,
  trackCapicandiaOnboardingStatusChecked,
  trackCapicandiaOnboardingPlanSelected,
} from "app/services/tracking/payments/checkoutStepsEvents";
import * as S from "./styles";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEP = {
  QUESTION: "question",
  EMAIL: "email",
  VERIFYING: "verifying",
  NOT_FOUND: "NOT_FOUND",
  ACTIVE: "ACTIVE",
  CANCELED: "CANCELED",
};

const PLAN_LABELS = {
  [CAPICANDIA_VARIANTS.CAPICANDIA_JUSFY]: "Capicand-IA + Jusfy Starter",
  [CAPICANDIA_VARIANTS.CAPICANDIA]: "Capicand-IA",
};

const PLAN_PRICES = {
  [CAPICANDIA_VARIANTS.CAPICANDIA_JUSFY]: {
    monthly: "R$ 116,90/mês",
    annually: "R$ 86,90/mês (R$ 1.042,80/ano)",
  },
  [CAPICANDIA_VARIANTS.CAPICANDIA]: {
    monthly: "R$ 97,00/mês",
    annually: "R$ 67,00/mês (R$ 804,00/ano)",
  },
};

const CapicandiaOnboarding = ({ coupom, formik, history, onComplete }) => {
  const [step, setStep] = useState(STEP.QUESTION);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [foundPlan, setFoundPlan] = useState(null);

  const activeCouponCode = coupom?.code || formik.values.coupom;
  const couponPeriodicity =
    getCapicandiaCheckoutPeriodicity(activeCouponCode) || "monthly";
  const isSpecialCoupon = isCapicandiaSpecialCoupon(activeCouponCode);

  const [selectedPeriodicity, setSelectedPeriodicity] = useState(
    couponPeriodicity
  );
  const [selectedVariant, setSelectedVariant] = useState(
    CAPICANDIA_VARIANTS.CAPICANDIA_JUSFY
  );
  const [generatingLink, setGeneratingLink] = useState(false);

  // O cupom da URL chega via LOAD_COUPOM (assíncrono), então na primeira render
  // couponPeriodicity ainda pode ser o fallback "monthly". Sincroniza o default do
  // seletor quando a periodicidade real do cupom resolve.
  useEffect(() => {
    if (couponPeriodicity) setSelectedPeriodicity(couponPeriodicity);
  }, [couponPeriodicity]);

  const handleNoAccount = () => {
    trackCapicandiaOnboardingAnswered({ has_account: false });
    onComplete();
  };

  const handleHasAccount = () => {
    trackCapicandiaOnboardingAnswered({ has_account: true });
    setStep(STEP.EMAIL);
  };

  const handleVerifyEmail = async () => {
    const value = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(value)) {
      setEmailError("Informe um e-mail válido.");
      return;
    }
    setEmailError("");
    setStep(STEP.VERIFYING);
    try {
      const response = await checkAccountStatus({ email: value });
      const data = response?.data?.data || response?.data || {};
      const status = data.status;
      setFoundPlan(data.plan || null);
      trackCapicandiaOnboardingStatusChecked({ status, plan: data.plan });
      if (status === "ACTIVE") setStep(STEP.ACTIVE);
      else if (status === "CANCELED") setStep(STEP.CANCELED);
      else {
        // Qualquer status fora do contrato (ex.: PAST_DUE, PENDING) cai no fluxo de
        // "conta não encontrada" e o usuário segue para um cadastro novo. Reporta
        // para termos visibilidade se o addon-manager passar a devolver outros status.
        if (status && status !== "NOT_FOUND") {
          Sentry.captureMessage(
            "capicandia_onboarding_unknown_subscription_status",
            { level: "warning", extra: { status } }
          );
        }
        setStep(STEP.NOT_FOUND);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: "capicandia_onboarding_status" },
      });
      toast.error(
        "Não foi possível verificar sua conta. Tente novamente."
      );
      setStep(STEP.EMAIL);
    }
  };

  const handleContinueRegistration = () => {
    formik.setFieldValue("email", email.trim().toLowerCase());
    onComplete();
  };

  // `periodicity` explícito porque a reativação pode redirecionar para cá depois de o
  // usuário escolher outro ciclo no seletor — nesse caso vale o escolhido, não o do cupom.
  const handleLoginToBuy = (periodicity = couponPeriodicity) => {
    const cycle = periodicity === "annually" ? "ANNUAL" : "MONTHLY";
    sessionStorage.setItem(
      POST_LOGIN_REDIRECT_KEY,
      `/addons/capicandia/checkout?cycle=${cycle}`
    );
    history.push("/auth/login");
  };

  const handleConfirmReactivation = async () => {
    if (generatingLink) return;
    setGeneratingLink(true);
    try {
      let planId;
      if (isSpecialCoupon) {
        planId =
          coupom?.plan_id || (await fetchCouponPlanId(activeCouponCode));
      } else {
        const selectedCode =
          CAPICANDIA_COUPONS[selectedVariant]?.[selectedPeriodicity]?.code;
        trackCapicandiaOnboardingPlanSelected({
          variant: selectedVariant,
          periodicity: selectedPeriodicity,
          coupon_code: selectedCode,
        });
        if (selectedCode === activeCouponCode && coupom?.plan_id) {
          planId = coupom.plan_id;
        } else {
          planId = await fetchCouponPlanId(selectedCode);
        }
      }
      if (!planId) throw new Error("Capicandia reactivation plan_id missing");
      const response = await requestReactivationPaymentLink({
        email: email.trim().toLowerCase(),
        plan: planId,
      });
      const action = response?.data?.action;
      if (action === "ADDON_CHECKOUT") {
        handleLoginToBuy(selectedPeriodicity);
        return;
      }
      const paymentLink = response?.data?.payment_link;
      if (!paymentLink) {
        throw new Error("Reactivation payment link missing in response");
      }
      window.location.href = paymentLink;
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: "capicandia_onboarding_reactivation" },
      });
      toast.error(
        "Não foi possível gerar o link de reativação. Tente novamente."
      );
      setGeneratingLink(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case STEP.QUESTION:
        return (
          <>
            <S.Kicker>Capicand-IA</S.Kicker>
            <S.Title>Você já tem conta na Jusfy?</S.Title>
            <S.Subtitle>
              Assim conseguimos direcionar você para a melhor forma de
              contratar a Capicand-IA.
            </S.Subtitle>
            <S.Actions>
              <S.OptionButton type="button" onClick={handleNoAccount}>
                Não, quero criar minha conta
              </S.OptionButton>
              <S.OptionButton type="button" onClick={handleHasAccount}>
                Sim, já tenho conta
              </S.OptionButton>
            </S.Actions>
          </>
        );
      case STEP.EMAIL:
        return (
          <>
            <S.Kicker>Capicand-IA</S.Kicker>
            <S.Title>Qual é o e-mail da sua conta?</S.Title>
            <S.Subtitle>
              Vamos verificar sua conta para indicar o melhor caminho.
            </S.Subtitle>
            <S.Field>
              <label htmlFor="onboarding-email">E-mail</label>
              <input
                id="onboarding-email"
                type="email"
                value={email}
                autoFocus
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") handleVerifyEmail();
                }}
                placeholder="seuemail@exemplo.com"
              />
              {emailError && <p className="err">{emailError}</p>}
            </S.Field>
            <S.Actions>
              <S.PrimaryButton type="button" onClick={handleVerifyEmail}>
                Verificar
              </S.PrimaryButton>
              <S.LinkButton type="button" onClick={() => setStep(STEP.QUESTION)}>
                Voltar
              </S.LinkButton>
            </S.Actions>
          </>
        );
      case STEP.VERIFYING:
        return (
          <S.Loading>
            <S.Spinner />
            <p>Estamos verificando suas informações...</p>
          </S.Loading>
        );
      case STEP.NOT_FOUND:
        return (
          <>
            <S.Kicker>Capicand-IA</S.Kicker>
            <S.Title>Não encontramos uma conta com este e-mail</S.Title>
            <S.Subtitle>
              Verificamos e o e-mail <strong>{email}</strong> ainda não possui
              cadastro na Jusfy. Você pode continuar seu cadastro normalmente.
            </S.Subtitle>
            <S.Actions>
              <S.PrimaryButton type="button" onClick={handleContinueRegistration}>
                Continuar meu cadastro
              </S.PrimaryButton>
              <S.SecondaryButton type="button" onClick={() => setStep(STEP.EMAIL)}>
                Tentar outro e-mail
              </S.SecondaryButton>
            </S.Actions>
          </>
        );
      case STEP.ACTIVE:
        return (
          <>
            <S.Kicker>Capicand-IA</S.Kicker>
            <S.Title>Você já tem uma conta ativa na Jusfy</S.Title>
            <S.Subtitle>
              {foundPlan ? (
                <>
                  Sua conta já conta com o plano <strong>{foundPlan}</strong>.{" "}
                </>
              ) : null}
              Faça login para verificar mais informações e adquirir a
              Capicand-IA como um complemento do seu plano atual — você não
              perde nada do que já tem.
            </S.Subtitle>
            <S.Actions>
              <S.PrimaryButton type="button" onClick={handleLoginToBuy}>
                Fazer login e contratar a Capicand-IA
              </S.PrimaryButton>
              <S.LinkButton type="button" onClick={() => setStep(STEP.EMAIL)}>
                Tentar outro e-mail
              </S.LinkButton>
            </S.Actions>
          </>
        );
      case STEP.CANCELED:
        return (
          <>
            <S.Kicker>Capicand-IA</S.Kicker>
            <S.Title>Sua conta está inativa</S.Title>
            <S.Subtitle>
              Escolha como quer voltar a usar a Jusfy com a Capicand-IA:
            </S.Subtitle>
            {!isSpecialCoupon && (
              <>
                <S.PeriodTabs>
                  <S.PeriodTab
                    type="button"
                    active={selectedPeriodicity === "monthly"}
                    onClick={() => setSelectedPeriodicity("monthly")}
                  >
                    Mensal
                  </S.PeriodTab>
                  <S.PeriodTab
                    type="button"
                    active={selectedPeriodicity === "annually"}
                    onClick={() => setSelectedPeriodicity("annually")}
                  >
                    Anual
                  </S.PeriodTab>
                </S.PeriodTabs>
                <S.Actions>
                  {[
                    CAPICANDIA_VARIANTS.CAPICANDIA_JUSFY,
                    CAPICANDIA_VARIANTS.CAPICANDIA,
                  ].map(variant => (
                    <S.PlanOption
                      key={variant}
                      type="button"
                      active={selectedVariant === variant}
                      onClick={() => setSelectedVariant(variant)}
                    >
                      {variant === CAPICANDIA_VARIANTS.CAPICANDIA_JUSFY && (
                        <span className="badge">Recomendado</span>
                      )}
                      <span className="name">{PLAN_LABELS[variant]}</span>
                      <span className="price">
                        <strong>
                          {PLAN_PRICES[variant][selectedPeriodicity]}
                        </strong>
                      </span>
                      <span className="radio" />
                    </S.PlanOption>
                  ))}
                </S.Actions>
              </>
            )}
            <S.Actions>
              <S.PrimaryButton
                type="button"
                onClick={handleConfirmReactivation}
                disabled={generatingLink}
              >
                {generatingLink ? "Gerando seu link..." : "Confirmar e continuar"}
              </S.PrimaryButton>
              <S.LinkButton type="button" onClick={() => setStep(STEP.EMAIL)}>
                Tentar outro e-mail
              </S.LinkButton>
            </S.Actions>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <S.Wrap>
      <S.Card>{renderStep()}</S.Card>
    </S.Wrap>
  );
};

export default CapicandiaOnboarding;
